import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';

import MainLayout from '../components/Layout/MainLayout';
import { useAuth } from '../contexts/AuthContext';
import useDebounce from '../hooks/useDebounce';
import useSubmissionProtection from '../hooks/useSubmissionProtection';
import {
  Input,
  Select,
  Button,
  Modal,
  DataTable,
  showToast,
  ConfirmDialog
} from '../components/UI';
import { rigService } from '../services/rigService';
import { Rig } from '../types/rig';

interface RigFormData {
  rig_no: string;
  isEdit?: boolean;
}

interface TableParams {
  page: number;
  limit: number;
  search: string;
}

// Validation schema
const rigSchema: yup.ObjectSchema<RigFormData> = yup.object().shape({
  rig_no: yup
    .string()
    .required('Rig Number is required')
    .min(1, 'Rig Number cannot be empty')
    .max(50, 'Rig Number must be less than 50 characters'),
  isEdit: yup.boolean().optional(),
});

const RigManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rigs, setRigs] = useState<Rig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRig, setEditingRig] = useState<Rig | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showActionMenu, setShowActionMenu] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; rig: Rig | null }>({
    show: false,
    rig: null
  });
  const [deleting, setDeleting] = useState(false);

  // Pagination and filter states
  const [tableParams, setTableParams] = useState<TableParams>({
    page: 1,
    limit: 10,
    search: ''
  });

  const [totalRigs, setTotalRigs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);

  const loadingRef = useRef(false);

  // Debounce search
  const debouncedSearch = useDebounce(searchInput, 500);

  // Form handling
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<RigFormData>({
    resolver: yupResolver(rigSchema),
    defaultValues: {
      rig_no: '',
      isEdit: false
    }
  });

  // Submission protection
  const { protectedSubmit } = useSubmissionProtection();

  // Load rigs with pagination and filters
  const loadRigs = useCallback(async (params: TableParams, sortingState?: SortingState) => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      setLoading(true);

      const response = await rigService.getRigs({
        page: params.page,
        limit: params.limit,
        search: params.search,
      });

      setRigs(response.data || []);
      setTotalRigs(response.pagination?.total || response.data.length);
      setTotalPages(response.pagination?.pages || 1);
    } catch (error: any) {
      console.error('Error loading rigs:', error);
      showToast.error(error.response?.data?.message || 'Failed to load rigs');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  // Effect for role-based access control - Admin only
  useEffect(() => {
    // Check if user has permission to access this page
    if (user && user.role !== 'ADMIN') {
      // Redirect to dashboard or show unauthorized message
      showToast.error('Only administrators can access Master/Rig management');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Effect for debounced search
  useEffect(() => {
    setTableParams(prev => ({
      ...prev,
      search: debouncedSearch,
      page: 1
    }));
  }, [debouncedSearch]);

  // Effect for loading data when params change - only if Admin
  useEffect(() => {
    if (user && user.role === 'ADMIN') {
      loadRigs(tableParams, sorting);
    }
  }, [tableParams, sorting, loadRigs, user]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePageChange = (page: number) => {
    setTableParams(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setTableParams(prev => ({ ...prev, limit, page: 1 }));
  };

  const handleCreate = () => {
    // Reset form first with empty values
    reset({
      rig_no: '',
      isEdit: false
    });
    setEditingRig(null);
    setShowModal(true);
  };

  const handleEdit = useCallback((rig: Rig) => {
    reset({
      rig_no: rig.rig_no,
      isEdit: true
    });
    setEditingRig(rig);
    setShowModal(true);
  }, [reset]);

  const handleDelete = (rig: Rig) => {
    setDeleteConfirm({ show: true, rig });
    setShowActionMenu(null);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.rig) return;

    try {
      setDeleting(true);
      await rigService.deleteRig(deleteConfirm.rig.id);
      showToast.success('Rig deleted successfully');
      setDeleteConfirm({ show: false, rig: null });
      loadRigs(tableParams, sorting);
    } catch (error: any) {
      console.error('Error deleting rig:', error);
      showToast.error(error.response?.data?.message || 'Failed to delete rig');
    } finally {
      setDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRig(null);
    reset();
  };

  const onSubmit = async (data: RigFormData) => {
    const submitFunction = async (formData: RigFormData) => {
      setSubmitting(true);
      try {
        const { isEdit, ...rigData } = formData;

        if (editingRig) {
          await rigService.updateRig(editingRig.id, rigData);
          showToast.success('Rig updated successfully');
        } else {
          await rigService.createRig(rigData);
          showToast.success('Rig created successfully');
        }

        // Close modal and reset form immediately
        handleCloseModal();
        loadRigs(tableParams, sorting);
      } finally {
        setSubmitting(false);
      }
    };

    try {
      await protectedSubmit(data, submitFunction);
    } catch (error: any) {
      console.error('Form submission error:', error);

      // Handle validation errors from backend
      if (error.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        Object.keys(backendErrors).forEach((field) => {
          setError(field as any, {
            type: 'server',
            message: backendErrors[field]
          });
        });
      } else {
        showToast.error(error.response?.data?.message || error.message || 'Failed to save rig');
      }

      setSubmitting(false);
    }
  };

  // Table columns
  const columns = useMemo<ColumnDef<Rig>[]>(() => [
    {
      accessorKey: 'id',
      header: 'S.No',
      cell: ({ row }) => (
        <div className="text-gray-900">{((tableParams.page - 1) * tableParams.limit) + row.index + 1}</div>
      ),
    },
    {
      accessorKey: 'rig_no',
      header: 'Rig Number',
      cell: ({ row }) => (
        <div className="font-medium text-gray-900">{row.original.rig_no}</div>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600">
          {new Date(row.original.created_at).toLocaleDateString()}
        </div>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated At',
      cell: ({ row }) => (
        <div className="text-sm text-gray-600">
          {new Date(row.original.updated_at).toLocaleDateString()}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {user?.role === 'ADMIN' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEdit(row.original)}
                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
              >
                <PencilIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(row.original)}
                className="text-red-600 hover:text-red-800 hover:bg-red-50"
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ], [user?.role, tableParams.page, tableParams.limit, handleEdit]);

  // Check for authorization before rendering - Admin only
  if (user && user.role !== 'ADMIN') {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Admin Access Only</h2>
            <p className="text-gray-600 mb-4">You do not have permission to access this page.</p>
            <p className="text-sm text-gray-500">Only Administrator can manage Rigs.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Rig Management</h1>
          <div className="flex items-center gap-4">
            {user?.role === 'ADMIN' && (
              <Button onClick={handleCreate} className="flex items-center gap-2">
                <PlusIcon className="w-5 h-5" />
                Add Rig
              </Button>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="max-w-md">
            <Input
              placeholder="Search rigs..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Data Table for Desktop */}
        {!isMobile ? (
          <div className="flex-1 min-h-0 bg-white rounded-2xl shadow-medium border border-gray-200 flex flex-col">
            <DataTable
              data={rigs}
              columns={columns}
              loading={loading}
              searchable={false}
              pageSize={tableParams.limit}
              showPagination={false}
              sorting={sorting}
              onSortingChange={setSorting}
            />

            {/* Custom Pagination */}
            <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-t border-gray-200 bg-white">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="text-sm text-gray-700 whitespace-nowrap">
                  Showing {((tableParams.page - 1) * tableParams.limit) + 1} to{' '}
                  {Math.min(tableParams.page * tableParams.limit, totalRigs)} of{' '}
                  {totalRigs} results
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
                  <div className="w-full sm:w-auto">
                    <Select
                      options={[
                        { value: '5', label: '5 per page' },
                        { value: '10', label: '10 per page' },
                        { value: '20', label: '20 per page' },
                        { value: '50', label: '50 per page' }
                      ]}
                      value={{ value: tableParams.limit.toString(), label: `${tableParams.limit} per page` }}
                      onChange={(option: any) => handlePageSizeChange(Number(option?.value))}
                      usePortal={false}
                      menuPlacement="top"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(tableParams.page - 1)}
                      disabled={tableParams.page === 1}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg min-w-[120px] justify-center">
                      Page {tableParams.page} of {totalPages}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePageChange(tableParams.page + 1)}
                      disabled={tableParams.page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Mobile Cards View
          <div className="flex-1 min-h-0 flex flex-col gap-4 relative">
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
                <div className="flex flex-col items-center p-6 bg-white rounded-2xl shadow-strong border border-gray-200">
                  <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                  <p className="text-gray-600 text-sm font-medium mt-3">Loading rigs...</p>
                </div>
              </div>
            )}
            <div className="flex-1 min-h-0 overflow-auto bg-white rounded-2xl shadow-medium border border-gray-200 p-4">
                <div className="space-y-3">
                {rigs.map((rig, index) => (
                  <div
                    key={rig.id}
                    className="bg-white rounded-2xl shadow-medium border border-gray-200 p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-sm text-gray-500 mb-1">
                          S.No: {((tableParams.page - 1) * tableParams.limit) + index + 1}
                        </div>
                        <div className="font-medium text-gray-900 text-lg">{rig.rig_no}</div>
                        <div className="text-sm text-gray-500 mt-2">
                          <div>Created: {new Date(rig.created_at).toLocaleDateString()}</div>
                          <div>Updated: {new Date(rig.updated_at).toLocaleDateString()}</div>
                        </div>
                      </div>

                      {user?.role === 'ADMIN' && (
                        <div className="relative">
                          <button
                            onClick={() => setShowActionMenu(showActionMenu === rig.id ? null : rig.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
                          </button>

                          {showActionMenu === rig.id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                              <button
                                onClick={() => {
                                  handleEdit(rig);
                                  setShowActionMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                              >
                                <PencilIcon className="w-4 h-4 mr-2" />
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  handleDelete(rig);
                                  setShowActionMenu(null);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                              >
                                <TrashIcon className="w-4 h-4 mr-2" />
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                </div>
            </div>

            {/* Mobile Pagination */}
            <div className="flex-shrink-0 bg-white rounded-2xl shadow-medium border border-gray-200 p-4">
              <div className="flex flex-col gap-4">
                <div className="text-sm text-gray-700 text-center">
                  Showing {((tableParams.page - 1) * tableParams.limit) + 1} to{' '}
                  {Math.min(tableParams.page * tableParams.limit, totalRigs)} of{' '}
                  {totalRigs} results
                </div>
                <div className="flex items-center justify-between gap-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(tableParams.page - 1)}
                    disabled={tableParams.page === 1}
                    className="flex-1"
                  >
                    Previous
                  </Button>
                  <div className="flex items-center px-3 py-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg">
                    Page {tableParams.page} of {totalPages}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(tableParams.page + 1)}
                    disabled={tableParams.page === totalPages}
                    className="flex-1"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingRig ? 'Edit Rig' : 'Add New Rig'}
        >
          <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-4">
            <Input
              label="Rig Number"
              {...register('rig_no')}
              error={errors.rig_no?.message}
              placeholder='Enter rig number'
              disabled={submitting}
            />

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                {editingRig ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, rig: null })}
          onConfirm={confirmDelete}
          title="Delete Rig"
          message={`Are you sure you want to delete the rig "${deleteConfirm.rig?.rig_no}"? This action cannot be undone.`}
          confirmText="Delete"
          variant="danger"
          loading={deleting}
        />
      </div>
    </MainLayout>
  );
};

export default RigManagement;
